import { inject, injectable } from '../../../../modules/common/di';
import { AdminApiDal } from '../adminApiDal';
import { ApiResponseBase } from '../../../models/apiResponseBase';
import { CreateNotificationGroupApiResponse, CreateNotificationGroupModel } from './createNotificationGroup';
import { GetNotificationGroupsApiResponse } from './getNotificationGroups';
import { UpdateNotificationUsersApiResponse, UpdateNotificationUsersModel } from './updateNotificationUsersApiResponse';
import { GetNotificationGroupAssignmentsApiResponse } from './getNotificationGroupAssignments';
import { UpdateNotificationGroupAssignmentsApiResponse, UpdateNotificationGroupAssignmentsModel } from './updateNotificationGroupAssignments';
import { testOrganizationNotificationsModel } from './testOrganizationNotifications';
import { ResponseType } from 'axios';

/**
 * Notifications API.
 * Administrators can manage user groups to receive notifications from end-user and organizational services.
 */
@injectable('NotificationsApi')
export class NotificationsApi {
  @inject('AdminApiDal') protected readonly dal!: AdminApiDal;

  /**
   * List organization notification groups.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Users-and-Locations/operation/Get%20Notification%20Groups}
   *
   * @param {number} organizationId Organization to get notification groups from.
   * @param [params] Requested parameters.
   * @param {number} [params.groupId] Optional group ID to get specific group.
   * @returns {Promise<GetNotificationGroupsApiResponse>}
   */
  getNotificationGroups(organizationId: number, params?: {
    groupId?: number;
  }): Promise<GetNotificationGroupsApiResponse> {
    return this.dal.get(`organizations/${encodeURIComponent(organizationId.toString())}/notificationGroups`, { params: params });
  }

  /**
   * Create or update organization notification group.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Users-and-Locations/operation/Create%20Notification%20Group}
   *
   * @param {number} organizationId Organization to create/update notification group.
   * @param {CreateNotificationGroupModel} group Notification group model.
   * @returns {Promise<CreateNotificationGroupApiResponse>}
   */
  createOrUpdateNotificationGroup(organizationId: number, group: CreateNotificationGroupModel): Promise<CreateNotificationGroupApiResponse> {
    const groupData = {
      group: group,
    };

    return this.dal.post(
      `organizations/${encodeURIComponent(organizationId.toString())}/notificationGroups`,
      groupData
    );
  }

  /**
   * Delete organization notification group.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Users-and-Locations/operation/Delete%20Notification%20Group}
   *
   * @param {number} organizationId Organization to delete notification group from.
   * @param params Requested parameters.
   * @param {number} params.groupId Mandatory group ID to delete.
   * @returns
   */
  deleteNotificationGroup(organizationId: number, params: {
    groupId: number,
  }): Promise<ApiResponseBase> {
    return this.dal.delete(
      `organizations/${encodeURIComponent(organizationId.toString())}/notificationGroups`,
      { params: params }
    );
  }

  /**
   * Manage notification users in notification groups.
   * @param {number} organizationId Organization to manage notification users.
   * @param {UpdateNotificationUsersModel} users Model of the users to update.
   * @returns {Promise<UpdateNotificationUsersApiResponse>}
   */
  updateNotificationUsers(
    organizationId: number,
    users: UpdateNotificationUsersModel,
  ): Promise<UpdateNotificationUsersApiResponse> {
    return this.dal.put(
      `organizations/${encodeURIComponent(organizationId.toString())}/notificationUsers`,
      users,
    );
  }

  /**
   * Get notification group assignments.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Users-and-Locations/operation/Get%20Notification%20Group%20Assignments}
   *
   * @param {number} organizationId Organization to get notification group assignments from.
   * @returns {Promise<GetNotificationGroupAssignmentsApiResponse>}
   */
  getNotificationGroupAssignments(organizationId: number): Promise<GetNotificationGroupAssignmentsApiResponse> {
    return this.dal.get(
      `organizations/${encodeURIComponent(organizationId.toString())}/notificationAssignments`
    );
  }

  /**
   * Update notification group assignments.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Users-and-Locations/operation/Update%20Notification%20Group%20Assignments
   *
   * @param {number} organizationId Organization to update notification group assignments.
   * @param {UpdateNotificationGroupAssignmentsModel} assignments Model of the assignments to update.
   * @returns {Promise<UpdateNotificationGroupAssignmentsApiResponse>}
   */
  updateNotificationGroupAssignments(
    organizationId: number,
    assignments: UpdateNotificationGroupAssignmentsModel,
  ): Promise<UpdateNotificationGroupAssignmentsApiResponse> {
    return this.dal.put(
      `organizations/${encodeURIComponent(organizationId.toString())}/notificationAssignments`,
      assignments,
    );
  }

  /**
   * Test organization notifications.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/admin.html#tag/Notifications/operation/Test%20notifications}
   *
   * @param {number} organizationId Organization to test notifications for.
   * @param {testOrganizationNotificationsModel} model Model of the notification to test.
   * @returns {Promise<ArrayBuffer|string|Object>}
   */
  testOrganizationNotifications(
    organizationId: number,
    model: testOrganizationNotificationsModel
  ): Promise<ArrayBuffer | string | object> {
    const contentType: ResponseType = 'blob';
    return this.dal.post(
      `organizations/${encodeURIComponent(organizationId.toString())}/notifications`,
      model,
      { responseType: contentType },
    );
  }

}
